import { wallOpeningOpening3 } from "building-common/openings/openingQuery";
import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGraphWallPlaneSideHandle } from "building-types/buildingGraph/buildingGraphSideHandles";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { WallPlaneRuleHandle } from "building-types/handles/docEntityHandleTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { WallPlaneDomainOperation, WallPlaneOperationAABB } from "building-types/walls2/wallPlane";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import { doorPlacementsOnLevel } from "doc/building/doorsOnLevel";
import { windowPlacementsOnLevel } from "doc/building/windowPlacementsOnLevel";
import { planarGraphHalfEdgeEnd, planarGraphHalfEdgeStart } from "geom/planarGraph";
import { planeCanonicalFromSegment, planeHash, planeNewXY, planeProject } from "geom/plane";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2AABB2 } from "geom/polyLine2";
import type { PolyLine3 } from "geom/polyLine3";

const LEVEL_XY_PLANE = planeNewXY();

const OPENING_WALL_CUT_OPERATOR = {
  operationType: "SET_WALL_CUT",
} as const;

export interface GenerateWallPlaneOpeningDomainOperationsParams {
  readonly windowPlacements: Building["windowPlacements"];
  readonly doorPlacements: Building["doorPlacements"];
  readonly buildingGraph: BuildingGraph;
  readonly planarGraphs: Building["planarGraphs"];
}

type ResolvedWallOpeningPlacement = GeneratedWindowPlacement | GeneratedDoorPlacement;

export function generateWallPlaneOpeningDomainOperations({
  windowPlacements,
  doorPlacements,
  buildingGraph,
  planarGraphs,
}: GenerateWallPlaneOpeningDomainOperationsParams): Record<string, WallPlaneDomainOperation[]> {
  const wallPlaneDomainOperations: Record<string, WallPlaneDomainOperation[]> = {};

  function addWallPlaneDomainOperation(wallPlaneHash: string, operation: WallPlaneDomainOperation) {
    const operations = wallPlaneDomainOperations[wallPlaneHash] ?? [];
    operations.push(operation);
    wallPlaneDomainOperations[wallPlaneHash] = operations;
  }

  for (const levelId in planarGraphs) {
    const levelPlanarGraph = planarGraphs[levelId];
    if (!levelPlanarGraph) {
      continue;
    }

    const placements: ResolvedWallOpeningPlacement[] = [
      ...windowPlacementsOnLevel(levelId, windowPlacements),
      ...doorPlacementsOnLevel(levelId, doorPlacements),
    ];

    for (const placement of placements) {
      collectResolvedWallOpeningWallCut({
        placement,
        levelPlanarGraph,
        buildingGraph,
        addWallPlaneDomainOperation,
      });
    }
  }

  return wallPlaneDomainOperations;
}

interface CollectResolvedWallOpeningWallCutParams {
  readonly placement: ResolvedWallOpeningPlacement;
  readonly levelPlanarGraph: PolygonPlanarGraph;
  readonly buildingGraph: BuildingGraph;
  readonly addWallPlaneDomainOperation: (wallPlaneHash: string, operation: WallPlaneDomainOperation) => void;
}

function collectResolvedWallOpeningWallCut({
  placement,
  levelPlanarGraph,
  buildingGraph,
  addWallPlaneDomainOperation,
}: CollectResolvedWallOpeningWallCutParams) {
  const opening = placement.opening;
  if (!opening) {
    return;
  }

  const wallPlaneHash = halfEdgeIndexGetWallPlaneHash(placement.halfEdgeIndex, levelPlanarGraph);
  if (!wallPlaneHash || !buildingGraph.buildingPlanes[wallPlaneHash]) {
    return;
  }

  const operationAABB = computeResolvedWallOpeningAABBInWallPlane(opening, wallPlaneHash, buildingGraph);
  if (!operationAABB) {
    return;
  }

  const faceSideHandle: BuildingGraphWallPlaneSideHandle = {
    type: "WALL_PLANE_SIDE",
    faceSource: {
      type: "WALL_PLANE",
      planeHash: wallPlaneHash,
    },
    faceDirection: "POSITIVE_NORMAL",
  };

  /**
   * @NOTE - source and rule handles are dummies, since
   * they are using placement ids rather than true doc rules and operations
   */
  const source: WallPlaneRuleHandle = {
    type: "WALL_PLANE_RULE",
    id: placement.handle?.id ?? placement.id,
  };

  const rule: WallPlaneRuleHandle = {
    type: "WALL_PLANE_RULE",
    id: placement.handle?.id ?? placement.id,
  };

  const wallPlaneDomainOperation: WallPlaneDomainOperation = {
    rule,
    source,
    wallPlaneOperator: OPENING_WALL_CUT_OPERATOR,
    wallPlaneFaceSideHandle: faceSideHandle,
    operationBoundary: { boundaryType: "AABB", aabb: operationAABB },
    resolvedFaceSide: "RIGHT", // Does not matter for wall cuts
  };

  addWallPlaneDomainOperation(wallPlaneHash, wallPlaneDomainOperation);
}

function halfEdgeIndexGetWallPlaneHash(
  halfEdgeIndex: number,
  levelPlanarGraph: PolygonPlanarGraph,
): string | undefined {
  const [startX, startY] = planarGraphHalfEdgeStart(levelPlanarGraph.planarGraph, halfEdgeIndex);
  const [endX, endY] = planarGraphHalfEdgeEnd(levelPlanarGraph.planarGraph, halfEdgeIndex);
  const canonicalPlane = planeCanonicalFromSegment(startX, startY, endX, endY, LEVEL_XY_PLANE);
  if (!canonicalPlane) {
    return undefined;
  }
  return planeHash(canonicalPlane);
}

function computeResolvedWallOpeningAABBInWallPlane(
  opening: WallOpening,
  wallPlaneHash: string,
  buildingGraph: BuildingGraph,
): WallPlaneOperationAABB | undefined {
  const wallPlane = buildingGraph.buildingPlanes[wallPlaneHash];
  if (!wallPlane) {
    return undefined;
  }

  const plane = wallPlane.planeFace.plane;
  const planeAABB = polyLine2AABB2(wallPlane.planeFace.loops[0]);
  const frontOutline3 = wallOpeningOpening3(opening, "FRONT", false);
  const backOutline3 = wallOpeningOpening3(opening, "BACK", false);

  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;

  for (const outline3 of [frontOutline3, backOutline3]) {
    if (!outline3) {
      continue;
    }
    projectPolyLine3OntoWallPlaneBounds(outline3, plane, (u, v) => {
      minU = Math.min(minU, u);
      maxU = Math.max(maxU, u);
      minV = Math.min(minV, v);
      maxV = Math.max(maxV, v);
    });
  }

  if (!Number.isFinite(minU) || !Number.isFinite(minV)) {
    return undefined;
  }

  return {
    start: { mode: "RELATIVE", value: planeAABB.minX - minU },
    end: { mode: "RELATIVE", value: maxU - planeAABB.maxX },
    bottom: { mode: "RELATIVE", value: planeAABB.minY - minV },
    top: { mode: "RELATIVE", value: maxV - planeAABB.maxY },
  };
}

function projectPolyLine3OntoWallPlaneBounds(
  outline3: PolyLine3,
  plane: Parameters<typeof planeProject>[0],
  emitProjectedPoint: (u: number, v: number) => void,
) {
  for (let i = 0; i < outline3.length; i += 3) {
    const [u, v] = planeProject(plane, outline3[i], outline3[i + 1], outline3[i + 2]);
    emitProjectedPoint(u, v);
  }
}
